import { Avatar as MuiAvatar } from '@mui/material';
import type { AvatarProps } from './types';
import { useMemo } from 'react';
import { palette } from 'styles/theme/palette';
import { Tooltip } from 'components/tooltip/Tooltip';

const getInitials = (letters?: string, size?: number) => {
  // this will accept all word first letter
  return (
    (letters?.split(' ') || [])
      .map(word => word[0])
      .filter(Boolean)
      .slice(0, size)
      .join('')
      .toUpperCase() ?? ''
  );
};

const sizeMap = {
  xs: 16,
  xsm: 20,
  xsl: 24,
  sm: 32,
  lg: 40,
  xl: 48,
  xxl: 56,
  xxxl: 72,
};

const fontSizeMap = {
  xs: 0.5,
  xsm: 0.625,
  xsl: 0.75,
  sm: 1,
  lg: 1.25,
  xl: 1.5,
  xxl: 1.75,
  xxxl: 2.25,
};

const maxLetterMap = {
  xs: 2,
  xsm: 2,
  xsl: 2,
  sm: 2,
  lg: 2,
  xl: 2,
  xxl: 2,
  xxxl: 2,
};

const colorMap = [
  palette.utility.marineBlue[400],
  palette.utility.lavender[800],
  palette.utility.coral[500],
  palette.utility.sunflower[700],
  palette.utility.brand[300],
  '#8A8DA4',
  '#E49CE9',
  '#B28481',
  '#FFCC65',
  '#63EBE1',
  '#4A538D',
  '#DD41E7',
  '#CF706C',
  '#887A5F',
  '#5B7E7B',
  '#5566E9',
];

// Hash function to generate a consistent index based on the letters value
const hashStringToIndex = (str: string, arrayLength: number): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % arrayLength;
};

export const Avatar = ({
  size = 'lg',
  src,
  letters,
  alt,
  color,
  tooltipTitle,
  tooltipProps,
  'data-testid': dataTestId = 'avatar',
}: AvatarProps) => {
  const backgroundColor = useMemo(() => {
    if (color) {
      return color;
    }
    if (letters) {
      const index = hashStringToIndex(letters, colorMap.length);
      return colorMap[index];
    }
    return colorMap[colorMap.length - 1];
  }, [color, letters]);

  const sxStyles = useMemo(
    () => ({
      width: `${sizeMap[size]}px !important`,
      height: sizeMap[size],
      fontSize: `${fontSizeMap[size]}rem`,
      backgroundColor,
    }),
    [size, backgroundColor]
  );

  const slicedLetters = useMemo(() => getInitials(letters, maxLetterMap[size]), [letters, size]);
  const avatar = (
    <MuiAvatar data-testid={dataTestId} sx={sxStyles} alt={alt} title={alt} src={src}>
      {letters ? slicedLetters : null}
    </MuiAvatar>
  );
  return tooltipTitle ? (
    <Tooltip {...tooltipProps} title={tooltipTitle}>
      {avatar}
    </Tooltip>
  ) : (
    avatar
  );
};
